import {
  ApplicationDto,
  ApplicationError,
  ApplicationPracticalExperienceDto,
  ApplicationPracticalExperienceError,
  SkillDto,
  SkillError,
} from "../types/applicationTypes";
import { isValidCountryCode, isValidPhoneNumber } from "./phoneUtils";

// Define scalar attributes for ApplicationError that are not arrays (e.g., internship, languages, etc.)
type ScalarAttributes = Exclude<
  keyof ApplicationError,
  "internship" | "hiwiStudentJob" | "voluntarySchool" | "voluntaryStudy" | "languages" | "itSkills"
>;

// Helper function to check if a date is invalid (null, undefined, or invalid date object)
const isDateInvalid = (dateValue: Date | null | undefined): boolean => {
  return dateValue === null || dateValue === undefined || isNaN(dateValue.getTime());
};

// Helper function to validate email format using a regular expression
const isEmailInvalid = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return !emailRegex.test(email);
};

// Helper function to validate name format (letters, spaces, hyphens, and apostrophes allowed)
const isNameInvalid = (name: string): boolean => {
  const nameRegex = /^[A-Za-zÀ-ÖØ-öø-ÿĀ-ž]+(?:[-'\s][A-Za-zÀ-ÖØ-öø-ÿĀ-ž]+)*$/;
  return !nameRegex.test(name);
};

// Helper function to validate birthdate (should not be in the future and should be at least 17 years ago)
const isBirthDateInvalid = (birthDate: Date | null | undefined): boolean => {
  if (birthDate instanceof Date) {
    const today = new Date();
    const minBirthDate = new Date(today.getFullYear() - 17, today.getMonth(), today.getDate());
    const maxBirthDate = new Date(today.getFullYear() - 40, today.getMonth(), today.getDate());
    if (birthDate > minBirthDate) return true; // if birthDate is in the future or less than 17 years ago return true indicating invalid
    if (birthDate < maxBirthDate) return true; // if birthDate is more than 40 years ago return true indicating invalid
  }
  return isDateInvalid(birthDate) || (birthDate !== null && birthDate !== undefined && birthDate > new Date());
};

// Helper function to validate phone number format
const isPhoneNumberInvalid = (phoneNumber: string, countryCode?: string | null): boolean => {
  return !isValidPhoneNumber(phoneNumber, countryCode);
};

// Helper function to validate country code format
const isCountryCodeInvalid = (countryCode: string): boolean => {
  return !isValidCountryCode(countryCode);
};

/**
 * Validates required attributes and sets error states accordingly.
 * @param attributes - List of attributes to check within the application state.
 * @param applicationState - Current state of the application data.
 * @param updateApplicationErrorState - Function to update error state with validation results.
 * @returns true if any required field is missing or invalid, false otherwise.
 */
const checkRequiredAttributes = (
  attributes: (keyof ApplicationDto)[],
  applicationState: ApplicationDto,
  updateApplicationErrorState: (updates: Partial<ApplicationError>) => void
): boolean => {
  let hasError = false; // Tracks if any error is found
  const newErrorState: Partial<ApplicationError> = {}; // Temporary error state object

  // Helper function to check if a field is empty or missing
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const isMissing = (value: any): boolean => value === "" || value === null || value === undefined;

  attributes.forEach((attribute) => {
    const value = applicationState[attribute];

    // Specific validation for email fields
    if (attribute === "email" || attribute === "confirmEmail") {
      const isError = isMissing(value) || isEmailInvalid(value as string);
      newErrorState[attribute] = !!isError;
      hasError ||= isError;

      // Specific validation for name fields (first and last names)
    } else if (attribute === "firstName" || attribute === "lastName") {
      const isError = isMissing(value) || isNameInvalid(value as string);
      newErrorState[attribute] = !!isError;
      hasError ||= isError;

      // Specific validation for birthdate
    } else if (attribute === "birthDate") {
      const isError = isMissing(value) || isBirthDateInvalid(value as Date);
      newErrorState.birthDate = !!isError;
      hasError ||= isError;

      // Specific validation for phone number
    } else if (attribute === "mobileCountryCode") {
      const isError = isMissing(value) || isCountryCodeInvalid(value as string);
      newErrorState.mobileCountryCode = !!isError;
      hasError ||= isError;

      // Specific validation for phone number
    } else if (attribute === "mobilePhone") {
      const isError = isMissing(value) || isPhoneNumberInvalid(value as string, applicationState.mobileCountryCode);
      newErrorState.mobilePhone = !!isError;
      hasError ||= isError;

      // Validation for practical experience arrays (e.g., internship and hiwiStudentJob)
    } else if (attribute === "internship" || attribute === "hiwiStudentJob") {
      const experienceArray = value as ApplicationPracticalExperienceDto[];
      const errors: ApplicationPracticalExperienceError[] = experienceArray.map((item) => ({
        id: item.id,
        activity: isMissing(item.activity),
        company: isMissing(item.company),
        location: isMissing(item.location),
        start: isDateInvalid(item.start),
        end: isDateInvalid(item.end),
      }));
      hasError ||= errors.some((error) => {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { id, ...rest } = error; // Destructure to exclude the id field
        return Object.values(rest).some((e) => e);
      });
      newErrorState[attribute] = errors;

      // Validation for skill arrays (e.g., languages and IT skills)
    } else if (attribute === "languages" || attribute === "itSkills") {
      const skillArray = value as SkillDto[];
      const errors: SkillError[] = skillArray.map((item) => ({
        id: item.id,
        name: isMissing(item.name),
        level: isMissing(item.level) || item.level === 0,
      }));
      hasError ||= errors.some((error) => {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { id, ...rest } = error; // Destructure to exclude the id field
        return Object.values(rest).some((e) => e);
      });
      newErrorState[attribute] = errors;

      // Conditional validation for occupation fields, based on applicationState.hasOccupation
    } else if (attribute === "occupation" && applicationState.hasOccupation) {
      const occupationErrors: Partial<ApplicationError> = {
        occupation: isMissing(applicationState.occupation),
        occupationCompany: isMissing(applicationState.occupationCompany),
        occupationLocation: isMissing(applicationState.occupationLocation),
        occupationStart: isDateInvalid(applicationState.occupationStart),
        occupationEnd: isDateInvalid(applicationState.occupationEnd),
      };
      hasError ||= Object.values(occupationErrors).some((e) => e);
      Object.assign(newErrorState, occupationErrors);

      // Conditional validation for apprenticeship fields, based on applicationState.apprenticeship
    } else if (attribute === "apprenticeship" && applicationState.apprenticeship) {
      const apprenticeshipErrors: Partial<ApplicationError> = {
        apprenticeshipJob: isMissing(applicationState.apprenticeshipJob),
        apprenticeshipCompany: isMissing(applicationState.apprenticeshipCompany),
        apprenticeshipLocation: isMissing(applicationState.apprenticeshipLocation),
        apprenticeshipStart: isDateInvalid(applicationState.apprenticeshipStart),
        apprenticeshipEnd: isDateInvalid(applicationState.apprenticeshipEnd),
      };
      hasError ||= Object.values(apprenticeshipErrors).some((e) => e);
      Object.assign(newErrorState, apprenticeshipErrors);

      // General validation for scalar attributes (non-array fields)
    } else {
      const isError = isMissing(value);
      newErrorState[attribute as ScalarAttributes] = !!isError;
      hasError ||= isError;
    }
  });

  // Update the application's error state with the validation results
  updateApplicationErrorState(newErrorState);
  return hasError;
};

/**
 * Checks required fields for the current application step and sets errors accordingly.
 * @param attributesToCheckByStep - Map of step numbers to required attributes for validation.
 * @param activeStep - The current active step of the application process.
 * @param applicationState - Current state of the application data.
 * @param updateApplicationErrorState - Function to update error state with validation results.
 * @returns true if any required field is missing or invalid, false otherwise.
 */
export const checkRequiredFields = (
  attributesToCheckByStep: { [key: number]: (keyof ApplicationDto)[] },
  activeStep: number,
  applicationState: ApplicationDto,
  updateApplicationErrorState: (updates: Partial<ApplicationError>) => void
): boolean => {
  const attributes = attributesToCheckByStep[activeStep] || [];

  // Add conditional attributes based on specific states (e.g., Master degree or apprenticeship)
  if (activeStep === 1) {
    if (applicationState.enrolledDegree === "Master") {
      attributes.push("bachelorSubject", "bachelorUniversity");
    }
    if (applicationState.apprenticeship) {
      attributes.push(
        "apprenticeshipJob",
        "apprenticeshipCompany",
        "apprenticeshipLocation",
        "apprenticeshipStart",
        "apprenticeshipEnd"
      );
    }
  } else if (activeStep === 2 && applicationState.hasOccupation) {
    attributes.push("occupation", "occupationCompany", "occupationLocation", "occupationStart", "occupationEnd");
  }

  // Perform validation on the collected attributes
  if (attributes.length > 0) {
    return checkRequiredAttributes(attributes, applicationState, updateApplicationErrorState);
  }
  return false;
};
